import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

class StatefulRouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return {
      error: error instanceof Error ? error : new Error('Unknown page error'),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route rendering failed', error, info);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <section className="flex min-h-[320px] items-center justify-center px-4" role="alert">
        <div className="w-full max-w-md rounded-lg border border-amber-200 bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-9 w-9 text-amber-600" aria-hidden="true" />
          <h1 className="mt-4 text-lg font-semibold text-gray-950">Page could not load</h1>
          <p className="mt-2 text-sm text-gray-600">
            A newer app version or temporary network problem may have interrupted navigation.
          </p>
          <Button className="mt-5" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Reload page
          </Button>
        </div>
      </section>
    );
  }
}

export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps) {
  const location = useLocation();

  return (
    // Reset only when the route (path) changes so a failed lazy load can retry. Keying on the query string
    // too remounted every page on each search-param change (typing into a URL-synced search box lost focus).
    <StatefulRouteErrorBoundary key={location.pathname}>
      {children}
    </StatefulRouteErrorBoundary>
  );
}
